import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { useToast } from '../ui/use-toast';

export const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const { signIn, completeNewPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [authStage, setAuthStage] = useState<'signIn' | 'newPassword'>('signIn');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (authStage === 'newPassword') {
        if (newPassword.length < 8) {
          throw new Error('New password must be at least 8 characters long.');
        }
        if (newPassword !== confirmNewPassword) {
          throw new Error('New password and confirmation must match.');
        }
        await completeNewPassword(newPassword);
        toast({
          variant: 'default',
          title: 'Password updated',
          description: 'Your password has been updated. Redirecting to the dashboard.',
        });
        navigate('/config');
        return;
      }

      const response = await signIn({ username: email, password });
      if (response.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setAuthStage('newPassword');
        setError('');
        toast({
          title: 'Password update required',
          description: 'Please create a new password to complete your first sign in.',
        });
        return;
      }

      navigate('/config');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sign in';
      setError(message);
      toast({
        variant: message.toLowerCase().includes('pending') ? 'default' : 'destructive',
        title: authStage === 'newPassword' ? 'Unable to update password' : 'Unable to sign in',
        description: message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        autoComplete="email"
        disabled={authStage === 'newPassword'}
      />
      
      {authStage === 'signIn' ? (
        <Input
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="current-password"
        />
      ) : (
        <>
          <div className="p-3 rounded-md bg-blue-50 text-blue-800 text-sm">
            A new password is required before you can continue. Please enter a strong password below.
          </div>
          <Input
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            autoComplete="new-password"
          />
          <Input
            label="Confirm New Password"
            type="password"
            value={confirmNewPassword}
            onChange={(e) => setConfirmNewPassword(e.target.value)}
            required
            autoComplete="new-password"
          />
        </>
      )}
      
      {error && (
        <div className="p-3 bg-red-50 text-red-700 rounded-md text-sm">
          {error}
        </div>
      )}
      
      <Button type="submit" disabled={loading} className="w-full">
        {loading
          ? authStage === 'newPassword'
            ? 'Saving...'
            : 'Signing in...'
          : authStage === 'newPassword'
            ? 'Set New Password'
            : 'Sign In'}
      </Button>
      
      <p className="text-center text-sm text-gray-600">
        Don&apos;t have an account?{' '}
        <button
          type="button"
          onClick={() => navigate('/register')}
          className="text-blue-600 hover:text-blue-500"
        >
          Sign up
        </button>
      </p>
    </form>
  );
};
